import React, { useEffect, useState, Component } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { Language } from '../translations';
type NavbarProps = {
  onOpenQuoteModal: () => void;
};
const languages: {
  code: Language;
  label: string;
  flag: string;
}[] = [{
  code: 'en',
  label: 'English',
  flag: '🇬🇧'
}, {
  code: 'zh',
  label: '简体中文',
  flag: '🇨🇳'
}, {
  code: 'id',
  label: 'Bahasa',
  flag: '🇮🇩'
}];
export function Navbar({
  onOpenQuoteModal
}: NavbarProps) {
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const navLinks = [{
    label: t.navbar.home,
    href: '#hero'
  }, {
    label: t.navbar.about,
    href: '#about'
  }, {
    label: t.navbar.products,
    href: '#products'
  }, {
    label: t.navbar.quality,
    href: '#quality'
  }, {
    label: t.navbar.contact,
    href: '#contact'
  }];
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    setIsLangMenuOpen(false);
  };
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Hello, I would like to request a quote for kratom products.');
    window.open(`https://wa.me/6282275757589?text=${message}`, '_blank');
  };
  const currentLanguage = languages.find(l => l.code === language);
  return <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`} role="navigation" aria-label="Main navigation" data-id="element-150">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-151">
        <div className="flex items-center justify-between h-16 md:h-20" data-id="element-152">
          {/* Logo */}
          <a href="#hero" onClick={e => {
          e.preventDefault();
          handleNavClick('#hero');
        }} className="flex items-center gap-3" data-id="element-153">
            <img src="https://cdn.magicpatterns.com/uploads/t37N25GpwJa9GYRmF4gvMx/1.png" alt="KBIN Logo" className="h-10 md:h-12 w-auto" data-id="element-154" />
            <span className={`font-bold text-lg md:text-xl transition-colors ${isScrolled ? 'text-emerald-800' : 'text-emerald-800'}`} data-id="element-155">
              KBIN
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8" data-id="element-156">
            {navLinks.map(link => <a key={link.href} href={link.href} onClick={e => {
            e.preventDefault();
            handleNavClick(link.href);
          }} className={`font-medium transition-colors hover:text-emerald-600 ${isScrolled ? 'text-gray-700' : 'text-gray-700'}`} data-id="element-157">
                {link.label}
              </a>)}

            {/* Language Selector */}
            <div className="relative" data-id="element-158">
              <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Select language" aria-expanded={isLangMenuOpen} data-id="element-159">
                <Globe className="w-5 h-5" data-id="element-160" />
                <span className="text-lg" data-id="element-161">{currentLanguage?.flag}</span>
              </button>

              {isLangMenuOpen && <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50" data-id="element-162">
                  {languages.map(lang => <button key={lang.code} onClick={() => handleLanguageChange(lang.code)} className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-emerald-50 transition-colors ${language === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'}`} data-id="element-163">
                      <span className="text-lg" data-id="element-164">{lang.flag}</span>
                      <span className="font-medium" data-id="element-165">{lang.label}</span>
                    </button>)}
                </div>}
            </div>

            <button onClick={handleWhatsAppClick} className="px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-xl transition-colors" data-id="element-166">
              {t.navbar.requestQuote}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMobileMenuOpen} data-id="element-167">
            {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-168" /> : <Menu className="w-6 h-6" data-id="element-169" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && <div className="md:hidden bg-white border-t border-gray-100 shadow-lg" data-id="element-170">
          <div className="px-4 py-4 space-y-2" data-id="element-171">
            {navLinks.map(link => <a key={link.href} href={link.href} onClick={e => {
          e.preventDefault();
          handleNavClick(link.href);
        }} className="block px-4 py-3 text-gray-700 font-medium hover:bg-emerald-50 hover:text-emerald-700 rounded-xl transition-colors" data-id="element-172">
                {link.label}
              </a>)}

            {/* Mobile Language Selector */}
            <div className="border-t border-gray-100 pt-2 mt-2" data-id="element-173">
              <div className="px-4 py-2 text-sm text-gray-500 font-medium flex items-center gap-2" data-id="element-174">
                <Globe className="w-4 h-4" data-id="element-175" />
                Language
              </div>
              {languages.map(lang => <button key={lang.code} onClick={() => {
            handleLanguageChange(lang.code);
            setIsMobileMenuOpen(false);
          }} className={`w-full px-4 py-2.5 text-left flex items-center gap-3 rounded-xl transition-colors ${language === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-176">
                  <span className="text-lg" data-id="element-177">{lang.flag}</span>
                  <span className="font-medium" data-id="element-178">{lang.label}</span>
                </button>)}
            </div>

            <button onClick={() => {
          setIsMobileMenuOpen(false);
          handleWhatsAppClick();
        }} className="w-full mt-2 px-4 py-3 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-xl transition-colors" data-id="element-179">
              {t.navbar.requestQuote}
            </button>
          </div>
        </div>}
    </nav>;
}